import { UserRoles } from '@dynamika-shared/types/auth';
import { OHIFViewerModeType } from '@dynamika-shared/types/viewerMode';
import { API_URL } from 'src/api/apiDictionary';

export const mapUserRoleToViewerMode = (
  selectedRole: UserRoles | null
): OHIFViewerModeType => {
  switch (selectedRole) {
    case UserRoles.READER:
    case UserRoles.SPONSOR:
    case UserRoles.EXTERNAL_USER:
    case UserRoles.PROJECT_MANAGER:
    case UserRoles.ADMIN:
      return OHIFViewerModeType.READER;
    case UserRoles.IMAGING_SPECIALIST:
      return OHIFViewerModeType.IMAGING_SPECIALIST;
    default:
      return OHIFViewerModeType.READER;
  }
};

export const mapUserRoleToDICOMResourceEndpoint = (
  selectedRole: UserRoles | null
): string => {
  switch (selectedRole) {
    case UserRoles.READER:
      return API_URL.readerDICOMResource;
    case UserRoles.SPONSOR:
    case UserRoles.EXTERNAL_USER:
    case UserRoles.PROJECT_MANAGER:
    case UserRoles.ADMIN:
      return API_URL.viewerDICOMResource;
    case UserRoles.IMAGING_SPECIALIST:
      return API_URL.DICOMResource;
    default:
      return API_URL.readerDICOMResource;
  }
};

export const mapUserRoleToTaskEndpoint = (
  selectedRole: UserRoles | null,
  hasParentTask: boolean = false
): string => {
  switch (selectedRole) {
    case UserRoles.READER:
      return hasParentTask ? API_URL.readerParentTask : API_URL.readerTask;
    case UserRoles.SPONSOR:
    case UserRoles.EXTERNAL_USER:
    case UserRoles.PROJECT_MANAGER:
    case UserRoles.ADMIN:
      return hasParentTask ? API_URL.viewerParentTask : API_URL.viewerTask;
    case UserRoles.IMAGING_SPECIALIST:
      return API_URL.qcTask;
    default:
      return hasParentTask ? API_URL.readerParentTask : API_URL.readerTask;
  }
};

/**
 * Where a role asks for every study of a grouped reading
 *
 * Plain terms
 * -----------
 * Only the people who actually open a reading — the reader, and the roles that
 * look over their shoulder — can ask for its images. Everyone else gets nothing
 * back from this function, and the app then makes no request on their behalf.
 *
 * Technical
 * ---------
 * Returns `null` rather than falling back to the reader URL the way its
 * neighbours do: this endpoint is called on a code path that is skipped
 * entirely when there is no endpoint, so a wrong guess would turn into a 403
 * instead of a no-op. `hasParentTask` has no counterpart here — the address is
 * always the parent id in a single segment (D7-7712, A1).
 */
export const mapUserRoleToDicomSeriesEndpoint = (
  selectedRole: UserRoles | null
): string | null => {
  switch (selectedRole) {
    case UserRoles.READER:
      return API_URL.readerReadingDicomSeries;
    case UserRoles.SPONSOR:
    case UserRoles.EXTERNAL_USER:
    case UserRoles.PROJECT_MANAGER:
    case UserRoles.ADMIN:
      return API_URL.viewerReadingDicomSeries;
    default:
      return null;
  }
};

export const mapUserRoleToTaskListEndpoint = (
  selectedRole: UserRoles | null
): string => {
  switch (selectedRole) {
    case UserRoles.READER:
      return API_URL.readerTasks;
    case UserRoles.SPONSOR:
    case UserRoles.EXTERNAL_USER:
    case UserRoles.ADMIN:
    case UserRoles.PROJECT_MANAGER:
      return API_URL.viewerTasks;
    case UserRoles.IMAGING_SPECIALIST:
      return API_URL.qcTasks;
    default:
      return API_URL.readerTasks;
  }
};
